import { useCallback, useEffect, useMemo } from "react";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { environmentSelector } from "Reducers/environment";
import { environmentServicesSelector } from "Reducers/metrics/services.selector";
import getMetrics from "src/organization/pages/project/environment/metrics/api/getMetrics";
import { getMetricQuery } from "src/organization/pages/project/environment/metrics/http/helper/index";
import { usePoller } from "src/organization/pages/project/environment/metrics/utils/polling";
import { useAppSelector } from "Store/hooks";

import { GRID_QUERY } from "../settings";
import { ResourceType, TimeFrameType } from "../types";

import { parseGridResource } from "./utils.legacy";

const req = (timeFrame: TimeFrameType, href: string, collection: string) => {
  const query = getMetricQuery({
    collection,
    timeframe: timeFrame,
    fields: GRID_QUERY
  });
  return getMetrics<ResourceType>(href, query);
};

const usePoll = ({ timeFrame }: { timeFrame: TimeFrameType }) => {
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();
  const environment = useSelectorWithUrlParams(environmentSelector);
  const betterServices = useAppSelector(state =>
    environmentServicesSelector(state, organizationId, projectId, environmentId)
  );

  const metricsLink = environment?.getLinks()?.["#metrics"]?.[0] as any;
  const collection = metricsLink?.collection;
  const href = metricsLink?.href;

  const requestFn = useCallback(
    () => req(timeFrame, href, collection),
    [timeFrame, href, collection]
  );

  const {
    data: result,
    poller,
    status,
    error,
    lastUpdated
  } = usePoller({
    requestFn,
    timeFrame,
    start: !!href
  });

  const { data, instances } = useMemo(
    () => parseGridResource(result, betterServices),
    [result, betterServices]
  );

  useEffect(() => {
    return () => poller.unsubscribe();
  }, [poller]);

  return { instances, data, status, error, lastUpdated };
};

export default usePoll;
